import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./rules-editor-alert-section-open.module.scss";
import { RulesEditorDeactivatableSection } from "./rules-editor-deactivatable-section";
import VelavuInput from "../../../elements/velavu-input";
import InfoTooltip from "../../../elements/info-tooltip";
import VelavuSwitchBlock from "../../../elements/velavu-switch-block";
import { RulesEditorEventErrorState } from "./state/input/rules-editor-error-state";
import { RulesEventInputState } from "./state/input/rules-event-input-state";
import { useVelavuModal } from "../../../elements/velavu-modal";
import RulesEditorSMSDisclaimerModal from "./rules-editor-sms-disclaimer-modal";

export interface RulesEditorAlertSectionOpenProps {
	className?: string;
	title: string;
	settings: RulesEventInputState;
	onChangeSettings: (settings: RulesEventInputState) => void;
	error: RulesEditorEventErrorState;
	onClose: () => void;
}

export default function RulesEditorAlertSectionOpen(
	props: RulesEditorAlertSectionOpenProps,
) {
	const { t } = useTranslation();
	const settings = props.settings;
	const changeSettings = props.onChangeSettings;

	const updateMessage = useCallback(
		(message: string) => {
			changeSettings?.({
				...settings,
				message,
			});
		},
		[settings, changeSettings],
	);

	const updatePhoneNumbers = useCallback(
		(phoneInput: string) => {
			changeSettings?.({
				...settings,
				phoneNumbers: phoneInput,
			});
		},
		[settings, changeSettings],
	);

	const updateEmailAddresses = useCallback(
		(emailInput: string) => {
			changeSettings?.({
				...settings,
				emailAddresses: emailInput,
			});
		},
		[settings, changeSettings],
	);

	const updateStoreAlert = useCallback(
		(storeAlert: boolean) => {
			changeSettings?.({
				...settings,
				storeAlert,
			});
		},
		[settings, changeSettings],
	);
	const updateStoreEvent = useCallback(
		(storeEvent: boolean) => {
			changeSettings?.({
				...settings,
				storeEvent,
			});
		},
		[settings, changeSettings],
	);

	const { pushModal } = useVelavuModal();
	const pushSMSDisclaimerModal = useCallback(() => {
		pushModal((close) => <RulesEditorSMSDisclaimerModal onClose={close} />);
	}, [pushModal]);

	return (
		<RulesEditorDeactivatableSection
			className={props.className}
			title={props.title}
			onDeactivate={props.onClose}
		>
			<div className={styles.container}>
				<VelavuInput
					style={{ gridColumn: "span 2" }}
					multiline
					rows={3}
					fullWidth
					placeholder={t("rules.editor.alert.message_placeholder")}
					titleText={
						<>
							<span>{t("rules.editor.alert.message")}</span>

							<InfoTooltip>
								<div className={styles.tooltipLayout}>
									<span className="p2m">
										{t(
											"rules.editor.alert.variable_tooltip",
										)}
									</span>
									<br />
									<span>
										&#123;&#123;device.id&#125;&#125;
									</span>
								</div>
							</InfoTooltip>
						</>
					}
					inputClassName="id16"
					value={props.settings.message}
					onChange={updateMessage}
					autoResize
					spellCheck={false}
				/>

				<div>
					<VelavuInput
						fullWidth
						placeholder="+12345678912"
						type="tel"
						spellCheck={false}
						titleText={
							<>
								<span>{t("rules.editor.alert.sms")}</span>

								<InfoTooltip>
									<div className={styles.tooltipLayout}>
										<span className="p2m">
											{t(
												"rules.editor.alert.e164_format",
											)}
										</span>
										<br />
										<span>+12345678912</span>
									</div>
								</InfoTooltip>
							</>
						}
						value={props.settings.phoneNumbers}
						onChange={updatePhoneNumbers}
						error={props.error.phoneNumbers}
					/>

					<span className={styles.inputSubtext}>
						<button
							className={styles.link}
							onClick={pushSMSDisclaimerModal}
						>
							{t("rules.editor.alert.disclaimer_link")}
						</button>
						{t("rules.editor.alert.sms_separator_hint")}
					</span>
				</div>

				<div>
					<VelavuInput
						fullWidth
						placeholder={t("rules.editor.alert.email_placeholder")}
						type="email"
						spellCheck={false}
						titleText={t("common.email_address")}
						value={props.settings.emailAddresses}
						onChange={updateEmailAddresses}
						error={props.error.emailAddresses}
					/>

					<span className={styles.inputSubtext}>
						{t("rules.editor.alert.email_separator_hint")}
					</span>
				</div>

				<VelavuSwitchBlock
					toggled={props.settings.storeAlert}
					onChange={updateStoreAlert}
					label={t("rules.editor.alert.visible_in_dashboard")}
				/>

				<VelavuSwitchBlock
					toggled={props.settings.storeEvent}
					onChange={updateStoreEvent}
					label={t("rules.editor.alert.save_events_to_db")}
				/>
			</div>
		</RulesEditorDeactivatableSection>
	);
}
